import type { AddCartItemInput, Cart, ProductId, UpdateCartItemInput } from '@sshop/domain';

import { parseDto } from '../../mappers/parseDto';
import type { Transport } from '../../transport/Transport';

import type { CartService } from './CartService';
import { cartDto } from './cart.dto';
import { toCart } from './cart.mapper';

/**
 * Laravel implementation of CartService.
 *
 * Endpoints:
 *   GET    /cart                       — current cart
 *   POST   /cart/items                 — add item (body: product_id, quantity)
 *   PATCH  /cart/items/{productId}     — change quantity (body: quantity)
 *   DELETE /cart/items/{productId}     — remove line
 *   DELETE /cart                       — clear cart
 *   POST   /cart/promo-code            — apply promo (body: code)
 *   DELETE /cart/promo-code/{code}     — remove promo
 *
 * Every endpoint returns the FULL updated cart so the client cache stays in
 * sync in a single round trip.
 */
export function createLaravelCartService(transport: Transport): CartService {
  const parse = (raw: unknown, context: string) => toCart(parseDto(cartDto, raw, context));

  return {
    async get(signal): Promise<Cart> {
      const res = await transport.request({
        method: 'GET',
        path: '/cart',
        ...(signal !== undefined ? { signal } : {}),
      });
      return parse(res.data, 'GET /cart');
    },

    async addItem(input: AddCartItemInput, signal): Promise<Cart> {
      const res = await transport.request({
        method: 'POST',
        path: '/cart/items',
        body: { product_id: input.productId, quantity: input.quantity },
        ...(signal !== undefined ? { signal } : {}),
      });
      return parse(res.data, 'POST /cart/items');
    },

    async updateItem(input: UpdateCartItemInput, signal): Promise<Cart> {
      const res = await transport.request({
        method: 'PATCH',
        path: `/cart/items/${encodeURIComponent(input.productId)}`,
        body: { quantity: input.quantity },
        ...(signal !== undefined ? { signal } : {}),
      });
      return parse(res.data, `PATCH /cart/items/${input.productId}`);
    },

    async removeItem(productId: ProductId, signal): Promise<Cart> {
      const res = await transport.request({
        method: 'DELETE',
        path: `/cart/items/${encodeURIComponent(productId)}`,
        ...(signal !== undefined ? { signal } : {}),
      });
      return parse(res.data, `DELETE /cart/items/${productId}`);
    },

    async addCollection(collectionId, quantity, selectedProducts = [], signal): Promise<Cart> {
      const res = await transport.request({
        method: 'POST', path: '/cart/collections',
        body: { collection_id: collectionId, quantity, selected_products: selectedProducts },
        ...(signal !== undefined ? { signal } : {}),
      });
      return parse(res.data, 'POST /cart/collections');
    },
    async updateCollection(collectionId, quantity, signal): Promise<Cart> {
      const res = await transport.request({
        method: 'PATCH', path: '/cart/collections/' + encodeURIComponent(collectionId),
        body: { quantity }, ...(signal !== undefined ? { signal } : {}),
      });
      return parse(res.data, 'PATCH /cart/collections');
    },
    async removeCollection(collectionId, signal): Promise<Cart> {
      const res = await transport.request({
        method: 'DELETE', path: '/cart/collections/' + encodeURIComponent(collectionId),
        ...(signal !== undefined ? { signal } : {}),
      });
      return parse(res.data, 'DELETE /cart/collections');
    },

    async clear(signal): Promise<Cart> {
      const res = await transport.request({
        method: 'DELETE',
        path: '/cart',
        ...(signal !== undefined ? { signal } : {}),
      });
      return parse(res.data, 'DELETE /cart');
    },

    async applyPromoCode(code: string, signal): Promise<Cart> {
      const res = await transport.request({
        method: 'POST',
        path: '/cart/promo-code',
        body: { code },
        ...(signal !== undefined ? { signal } : {}),
      });
      return parse(res.data, 'POST /cart/promo-code');
    },

    async removePromoCode(code: string, signal): Promise<Cart> {
      const res = await transport.request({
        method: 'DELETE',
        path: `/cart/promo-code/${encodeURIComponent(code)}`,
        ...(signal !== undefined ? { signal } : {}),
      });
      return parse(res.data, `DELETE /cart/promo-code/${code}`);
    },
  };
}
